import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Divider, Form, Input, Modal, Steps, Typography, message } from "antd";
import { CheckCircleFilled, LockOutlined, LoginOutlined, RocketOutlined } from "@ant-design/icons";
import {
  useAcceptInvitationMutation,
  useValidateInvitationMutation,
} from "../graphql/generated/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PASSWORD_RULES } from "../common/constants";

function ConfirmInvite() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const params = useParams();
  const [messageApi, contextHolder] = message.useMessage();
  const lastValidatedTokenRef = useRef<string | null>(null);
  const [storeAdminModalOpen, setStoreAdminModalOpen] = useState(false);

  const [validateInvitation] = useValidateInvitationMutation();
  const [acceptInvitation, { loading }] = useAcceptInvitationMutation();

  useEffect(() => {
    const token = params.token;
    if (!token) return;

    // React 18 StrictMode runs effects twice in development.
    // This guard ensures we validate only once per token.
    if (lastValidatedTokenRef.current === token) return;
    lastValidatedTokenRef.current = token;

    validateInvitation({ variables: { token } })
      .then((res) => {
        if (!res.data?.validateInvitation) {
          navigate("/login");
        }
      })
      .catch(() => {
        navigate("/login");
      });
  }, [params.token, navigate, validateInvitation]);

  const onFinish = (values: any) => {
    if (params.token && values.password) {
      acceptInvitation({
        variables: {
          token: params.token,
          password: values.password,
        },
      }).then((res) => {
        const result = res.data?.acceptInvitation;
        if (result?.success) {
          if (result.storeId != null) {
            setStoreAdminModalOpen(true);
          } else {
            navigate("/login");
          }
        } else {
          messageApi.error(t("message.error.accept_invitation_failed"));
        }
      });
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    // Form validation failed
  };

  return (
    <div style={{ maxWidth: "300px", margin: "auto", padding: "50px 0" }}>
      {contextHolder}
      <Modal
        open={storeAdminModalOpen}
        closable={false}
        footer={null}
        width={480}
        centered
      >
        <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
          <CheckCircleFilled style={{ fontSize: 56, color: "#52c41a" }} />
          <Typography.Title level={3} style={{ marginTop: 16, marginBottom: 4 }}>
            {t("modal.store_admin_password_reset.title")}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t("modal.store_admin_password_reset.content")}
          </Typography.Text>
        </div>

        <Divider />

        <Typography.Text strong style={{ display: "block", marginBottom: 16 }}>
          {t("modal.store_admin_password_reset.next_steps")}
        </Typography.Text>

        <Steps
          direction="vertical"
          size="small"
          current={3}
          items={[
            {
              title: t("modal.store_admin_password_reset.step1.title"),
              description: t("modal.store_admin_password_reset.step1.description"),
              icon: <RocketOutlined />,
            },
            {
              title: t("modal.store_admin_password_reset.step2.title"),
              description: t("modal.store_admin_password_reset.step2.description"),
              icon: <LoginOutlined />,
            },
            {
              title: t("modal.store_admin_password_reset.step3.title"),
              description: t("modal.store_admin_password_reset.step3.description"),
              icon: <LockOutlined />,
            },
          ]}
        />

        <Alert
          type="info"
          showIcon
          message={t("modal.store_admin_password_reset.note")}
          style={{ marginTop: 16 }}
        />
      </Modal>
      <Form
        dir={i18n.dir()}
        name="confirm-invite"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          name="password"
          rules={[
            { required: true, message: t("message.error.password_required") },
            ...PASSWORD_RULES.map((rec) => ({
              ...rec,
              message: t(rec.message),
            })),
          ]}
          hasFeedback
        >
          <Input.Password placeholder={t("placeholder.password")} />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          dependencies={["password"]}
          hasFeedback
          rules={[
            { required: true, message: t("message.error.field_required") },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("The password that you entered do not match!")
                );
              },
            }),
          ]}
        >
          <Input.Password placeholder={t("placeholder.confirm_password")} />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button loading={loading} type="primary" htmlType="submit">
            {t("button.confirm")}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}

export default ConfirmInvite;
